import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { ActivityFilters, ActivityItem, TimelineEntityType } from '../types';

/** Normalize a raw activity record (fields differ slightly across endpoints). */
interface RawActivity {
  id: string;
  action?: string;
  action_type?: string;
  detail?: string;
  details?: string;
  display_name?: string;
  role?: string;
  entity_type?: string;
  entity_id?: string;
  created_at: string;
}

function normalize(r: RawActivity): ActivityItem {
  return {
    id: r.id,
    action: r.action ?? r.action_type ?? '',
    detail: r.detail ?? r.details ?? '',
    actor: r.display_name,
    role: r.role,
    entity_type: r.entity_type,
    entity_id: r.entity_id != null ? String(r.entity_id) : undefined,
    created_at: r.created_at,
  };
}

/** Global / department / own activity feed (backend scopes by role). */
export function useActivityFeed(filters: ActivityFilters) {
  return useQuery({
    queryKey: ['activity', 'feed', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.action) params.set('action', filters.action);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.userId) params.set('userId', filters.userId);
      if (!filters.action && filters.exclude?.length) params.set('exclude', filters.exclude.join(','));
      params.set('limit', String(filters.limit));
      params.set('offset', String(filters.offset));
      const { data } = await api.get<{ activity: RawActivity[] }>(`/api/activity?${params.toString()}`);
      return (data.activity ?? []).map(normalize);
    },
    placeholderData: keepPreviousData,
  });
}

/** Per-entity timeline — only lead/deal are backend-supported. */
export function useEntityActivity(type: TimelineEntityType, id: string | null) {
  return useQuery({
    queryKey: ['activity', 'entity', type, id],
    queryFn: async () => {
      const { data } = await api.get<{ logs: RawActivity[] }>(`/api/activity/entity/${type}/${id}`);
      return (data.logs ?? []).map(normalize);
    },
    enabled: !!id,
  });
}
